import { useAuth } from '../context/AuthContext';

/**
 * Hook personalizado para verificar permisos basados en roles
 * @returns Objeto con funciones para verificar permisos
 */
export const useRoleAccess = () => {
  const { currentUser } = useAuth();
  const userRole = currentUser?.role?.toLowerCase() || 'guest'; // Normalizar a minúsculas

  /**
   * Verifica si el usuario tiene permiso para CREAR nuevas entidades
   * @returns {boolean} true si tiene permiso, false en caso contrario
   */
  const canCreate = () => {
    if (!currentUser || !currentUser.role) return false;
    // Los roles del sistema son admin, manager, facturador y visor. «gerente» no
    // existe en el backend: figuraba acá y en las rutas, y por eso un manager no
    // veía Usuarios ni Configuración.
    // Admin, manager y facturador pueden crear
    return ['admin', 'manager', 'facturador'].includes(userRole);
  };

  /**
   * Verifica si el usuario tiene permiso para EDITAR entidades existentes
   * @returns {boolean} true si tiene permiso, false en caso contrario
   */
  const canEdit = () => {
    if (!currentUser || !currentUser.role) return false;
    // El rol "visor" solo puede ver, no editar
    if (userRole === 'visor') return false;
    // Los demás (admin, manager, facturador) pueden editar
    return ['admin', 'manager', 'facturador'].includes(userRole);
  };

  /**
   * Verifica si el usuario tiene permiso para ELIMINAR entidades
   * @returns {boolean} true si tiene permiso, false en caso contrario
   */
  const canDelete = () => {
    if (!currentUser || !currentUser.role) return false;
    // Admin y manager pueden eliminar
    return ['admin', 'manager'].includes(userRole);
  };

  /**
   * Verifica si el usuario tiene permiso para acceder a configuración avanzada
   * @returns {boolean} true si tiene permiso, false en caso contrario
   */
  const canAccessAdvancedSettings = () => {
    if (!currentUser || !currentUser.role) return false;
    // Admin y manager acceden a la configuración avanzada
    return ['admin', 'manager'].includes(userRole);
  };

  /**
   * Verifica si el usuario tiene un rol específico
   * @param {string} role - El rol a verificar (se compara en minúsculas)
   * @returns {boolean} true si tiene el rol, false en caso contrario
   */
  const hasRole = (role) => {
    if (!currentUser || !currentUser.role) return false;
    return userRole === role.toLowerCase();
  };

  return {
    canCreate, // Exportar la nueva función
    canEdit,
    canDelete,
    canAccessAdvancedSettings,
    hasRole,
    userRole: userRole // Devolver el rol normalizado
  };
};

export default useRoleAccess;